import * as db from '@/core/Database'
import { BaseStateManager } from '@/services/BaseStateManager'
import { Company, CompanyData } from '@/core/Types'
import { AuthStateData, UserManager } from '@/services/UserManager'
import { DocSnapTyped } from '@/extern/FirebaseTypes'

export interface UserCompanyState {
    auth: AuthStateData|undefined
    company: Company|undefined
}

/**
 * Monitors the active user and streams the company (if any) associated with the user.
 */
export class UserCompanyManager extends BaseStateManager<UserCompanyState> {
    companyStreamUnsubscriber: VoidFunction|null = null

    constructor(private readonly userManager: UserManager) {
        super({auth: undefined, company: undefined});

        userManager.stateStream.subscribe(state => this.onUserStateChanged(state))
    }

    private onUserStateChanged(authState: AuthStateData) {
        const newCompanyId = authState?.user?.data?.companyId
        const existingCompanyId = this.state?.company?.id

        // if the company to be streamed changed, reset the streams
        if (newCompanyId !== existingCompanyId)
            this.resetCompanyStream(newCompanyId)

        this._setAndPublishState({
            auth: authState,
            company: (newCompanyId === existingCompanyId) ? this.state?.company : undefined
        })
    }

    private resetCompanyStream(newCompanyId: string|undefined) {
        // cancel old stream
        if (this.companyStreamUnsubscriber) {
            this.companyStreamUnsubscriber()
            this.companyStreamUnsubscriber = null
        }

        // start new stream if this user has a company
        if (newCompanyId) {
            this.companyStreamUnsubscriber = db.streamCompanyData(newCompanyId,
                snapshot => this.onCompanyDataReceived(newCompanyId, snapshot)
            )
        }
    }

    private onCompanyDataReceived(companyId: string, snapshot: DocSnapTyped<CompanyData>) {
        const userCompanyId = this.state?.auth?.user?.data?.companyId
        if (userCompanyId === companyId) {
            const companyData = snapshot.data
            let company: Company|undefined
            if (companyData) {
                company = {
                    id: companyId,
                    data: companyData
                }
            }
            this._setAndPublishState({
                auth: this.state.auth,
                company: company
            })
        }
    }
}